import React, { useState, useRef } from 'react';
import './UniversalUpload.css';

const UniversalUpload = ({ onFileUpload, isProcessing }) => {
  const [dragOver, setDragOver] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const fileInputRef = useRef(null);

  const allowedTypes = ['image/jpeg', 'image/png', 'image/tiff', 'application/pdf'];
  const maxSize = 10 * 1024 * 1024; // 10MB
  const maxFiles = 20;

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelection(files);
    }
  };

  const handleFileInputChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      handleFileSelection(files);
    }
  };

  const handleFileSelection = (newFiles) => {
    const validFiles = [];
    const errors = [];

    // Check total file count
    if (selectedFiles.length + newFiles.length > maxFiles) {
      errors.push(`Максимален брой файлове: ${maxFiles}`);
      newFiles = newFiles.slice(0, maxFiles - selectedFiles.length);
    }

    newFiles.forEach(file => {
      // Check if file already selected
      if (selectedFiles.some(f => f.name === file.name && f.size === file.size)) {
        errors.push(`${file.name}: Файлът вече е избран`);
        return;
      }

      // Validate file type
      if (!allowedTypes.includes(file.type)) {
        errors.push(`${file.name}: Поддържат се само JPEG, PNG, TIFF и PDF файлове`);
        return;
      }

      // Validate file size
      if (file.size > maxSize) {
        errors.push(`${file.name}: Файлът е прекалено голям (макс. 10MB)`);
        return;
      }

      validFiles.push(file);
    });

    if (errors.length > 0) {
      alert(`Грешки при избор на файлове:\n${errors.join('\n')}`);
    }

    if (validFiles.length > 0) {
      setSelectedFiles(prev => [...prev, ...validFiles]);
    }
  };

  const removeFile = (index) => {
    setSelectedFiles(files => files.filter((_, i) => i !== index));
  };

  const clearAllFiles = () => {
    setSelectedFiles([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUpload = () => {
    if (selectedFiles.length === 0) return;
    
    onFileUpload(selectedFiles);
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (fileType) => {
    if (fileType === 'application/pdf') return '📄';
    if (fileType.startsWith('image/')) return '🖼️';
    return '📁';
  };

  const getTotalSize = () => {
    return selectedFiles.reduce((total, file) => total + file.size, 0);
  };

  return (
    <div className="universal-upload-container">
      <div className="upload-header">
        <h2>📁 Качете фактури</h2>
        <p className="upload-subtitle">
          Поддържа един файл или много файлове наведнъж<br />
          <span className="upload-help">Supports single file or multiple files at once</span>
        </p>
      </div>
      
      <div
        className={`upload-area ${dragOver ? 'drag-over' : ''} ${selectedFiles.length > 0 ? 'has-files' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={allowedTypes.join(',')}
          onChange={handleFileInputChange}
          style={{ display: 'none' }}
        />

        <div className="upload-icon">
          {selectedFiles.length === 0 ? '📤' : '✅'}
        </div>
        
        {selectedFiles.length === 0 ? (
          <>
            <h3>Влачете и пуснете файлове тук</h3>
            <p>или кликнете за избор на файлове</p>
            <p className="upload-help">
              <strong>Drag & drop files here</strong><br />
              or click to select files
            </p>
            <div className="file-requirements">
              <div className="requirement-item">
                <span className="icon">📄</span>
                <span>PDF файлове с множество фактури</span>
              </div>
              <div className="requirement-item">
                <span className="icon">🖼️</span>
                <span>Снимки на фактури (JPEG, PNG, TIFF)</span>
              </div>
              <div className="requirement-item">
                <span className="icon">📊</span>
                <span>Максимум {maxFiles} файла, до 10MB всеки</span>
              </div>
            </div>
          </>
        ) : (
          <div className="add-more-files">
            <h4>➕ Добавете още файлове</h4>
            <p>Или влачете тук за добавяне на още</p>
          </div>
        )}
      </div>

      {selectedFiles.length > 0 && (
        <div className="selected-files">
          <div className="files-header">
            <h3>Избрани файлове ({selectedFiles.length})</h3>
            <div className="files-summary">
              <span className="total-size">Общ размер: {formatFileSize(getTotalSize())}</span>
              <button 
                type="button" 
                onClick={clearAllFiles} 
                className="clear-all-btn"
                disabled={isProcessing}
              >
                🗑️ Изчисти всички
              </button>
            </div>
          </div>
          
          <div className="files-list">
            {selectedFiles.map((file, index) => (
              <div key={`${file.name}-${index}`} className="file-item">
                <div className="file-icon-type">
                  {getFileIcon(file.type)}
                </div>
                <div className="file-details">
                  <div className="file-name">{file.name}</div>
                  <div className="file-meta">
                    {formatFileSize(file.size)} • {file.type.split('/')[1].toUpperCase()}
                  </div>
                </div>
                <button 
                  type="button" 
                  onClick={() => removeFile(index)}
                  className="remove-file-btn"
                  disabled={isProcessing}
                >
                  ❌
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="upload-actions">
        {selectedFiles.length > 0 && (
          <button 
            onClick={handleUpload}
            disabled={isProcessing}
            className="upload-btn"
          >
            {isProcessing ? (
              <>
                <span className="spinner">⏳</span>
                Обработване на {selectedFiles.length} файла...
              </>
            ) : (
              <>
                🚀 Обработи {selectedFiles.length} {selectedFiles.length === 1 ? 'файл' : 'файла'}
              </>
            )}
          </button>
        )}
        
        {selectedFiles.length === 0 && (
          <div className="upload-tips">
            <h4>💡 Съвети:</h4>
            <ul>
              <li>За <strong>една фактура</strong>: качете една снимка или PDF</li>
              <li>За <strong>много фактури</strong>: качете много файлове наведнъж</li>
              <li>PDF файловете могат да съдържат множество фактури</li>
              <li>Системата автоматично разпознава типа обработка</li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default UniversalUpload;
